<template>
  <div class="container-fluid mt-4" :class="themeClass">
    <div class="card shadow-sm">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <i class="bi bi-pencil me-2"></i>
          Editar Cliente
        </h5>
        <div>
          <router-link :to="`/personas/clientes/ver/${clienteId}`" class="btn btn-info me-2">
            <i class="bi bi-eye me-1"></i>
            Ver
          </router-link>
          <router-link to="/personas/clientes" class="btn btn-secondary">
            <i class="bi bi-arrow-left me-1"></i>
            Volver al Listado
          </router-link>
        </div>
      </div>
      <div class="card-body">
        <!-- Mensaje de error -->
        <div v-if="error" class="alert alert-danger" role="alert">
          <i class="bi bi-exclamation-triangle me-2"></i>
          {{ error }}
          <button type="button" class="btn btn-sm btn-outline-danger ms-2" @click="loadCliente">
            <i class="bi bi-arrow-clockwise"></i> Reintentar
          </button>
        </div>

        <!-- Indicador de carga -->
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
          <p class="mt-2 text-muted">Cargando cliente...</p>
        </div>

        <!-- Formulario de edición -->
        <form v-else-if="cliente" @submit.prevent="guardarCliente">
          <!-- Sección Identificación -->
          <div class="row mb-3">
            <div class="col-md-4">
              <label for="tipoId" class="form-label">Tipo de identificación <span class="text-danger">*</span></label>
              <select v-model="cliente.tipoId" id="tipoId" class="form-select" required>
                <option value="">Seleccione</option>
                <option v-for="tipo in tiposIdentificacion" :key="tipo.id" :value="tipo.codigo">
                  {{ tipo.tipo_de_identificacion }}
                </option>
              </select>
            </div>
            <div class="col-md-4">
              <label for="identificacion" class="form-label">Identificación <span class="text-danger">*</span></label>
              <input
                v-model="cliente.identificacion"
                type="text"
                id="identificacion"
                class="form-control"
                required
              />
            </div>
            <div class="col-md-4">
              <label for="razonSocial" class="form-label">Razón Social / Nombre <span class="text-danger">*</span></label>
              <input v-model="cliente.razonSocial" type="text" id="razonSocial" class="form-control" required />
            </div>
          </div>

          <!-- Sección Contacto y Dirección -->
          <div class="row mb-3">
            <div class="col-md-6">
              <label for="correo" class="form-label">Correo electrónico</label>
              <input v-model="cliente.correo" type="email" id="correo" class="form-control" />
            </div>
            <div class="col-md-6">
              <label for="telefono" class="form-label">Teléfono</label>
              <input v-model="cliente.telefono" type="text" id="telefono" class="form-control" />
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-md-12">
              <label for="direccion" class="form-label">Dirección</label>
              <input v-model="cliente.direccion" type="text" id="direccion" class="form-control" />
            </div>
          </div>

          <div class="d-grid d-md-flex justify-content-md-end mt-4">
            <button type="button" class="btn btn-secondary me-2" @click="cancelar">
              <i class="bi bi-x-lg me-1"></i> Cancelar
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <i class="bi bi-check-lg me-1"></i>
              {{ saving ? 'Guardando...' : 'Guardar Cambios' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import themeManager from '@/store/theme'
import { clientService } from '@/services/clientService'
import api from '@/services/api'
import notificationService from '@/services/notificationService'

export default {
  name: 'ClientClientesEditar',
  props: ['id'],
  computed: {
    themeClass() {
      return themeManager.themeClass
    },
    clienteId() {
      return this.$route.params.id || this.id
    }
  },
  data() {
    return {
      tiposIdentificacion: [],
      cliente: null,
      loading: false,
      saving: false,
      error: null
    }
  },
  async mounted() {
    this.fetchTiposIdentificacion()
    await this.loadCliente()
  },
  methods: {
    async fetchTiposIdentificacion() {
      try {
        const res = await api.get('/tipos-identificacion-sri')
        this.tiposIdentificacion = res.data?.data || []
      } catch (e) {
        console.error('Error cargando tipos de identificación', e)
      }
    },
    async loadCliente() {
      try {
        this.loading = true
        this.error = null

        const response = await clientService.getClient(this.clienteId)
        this.cliente = this.mapClienteData(response.data)

      } catch (error) {
        console.error('Error loading client:', error)
        this.error = 'Error al cargar el cliente'
        this.cliente = null
      } finally {
        this.loading = false
      }
    },

    mapClienteData(apiData) {
      return {
        id: apiData.id,
        tipoId: apiData.tipo_identificacion || '',
        identificacion: apiData.identification_number || '',
        razonSocial: apiData.business_name || '',
        telefono: apiData.phone || '',
        correo: apiData.email || '',
        direccion: apiData.address || ''
      }
    },

    async guardarCliente() {
      try {
        this.saving = true
        this.error = null

        if (!this.cliente.razonSocial.trim()) {
          this.error = 'El nombre / razón social es requerido'
          return
        }
        if (!this.cliente.identificacion.trim()) {
          this.error = 'La identificación es requerida'
          return
        }

        const clientData = {
          tipo_identificacion: this.cliente.tipoId,
          identification_number: this.cliente.identificacion.trim(),
          business_name: this.cliente.razonSocial.trim(),
          phone: this.cliente.telefono ? this.cliente.telefono.trim() : '',
          email: this.cliente.correo ? this.cliente.correo.trim() : '',
          address: this.cliente.direccion ? this.cliente.direccion.trim() : ''
        }

        await clientService.updateClient(this.cliente.id, clientData)
        notificationService.success('Cliente actualizado exitosamente');
        this.$router.push('/personas/clientes')

      } catch (error) {
        const errorData = error.response?.data
        this.error = errorData?.error || 'Error al guardar los cambios'
      } finally {
        this.saving = false
      }
    },

    cancelar() {
      this.$router.push(`/personas/clientes/ver/${this.clienteId}`)
    },

    mostrarNotificacion(mensaje, tipo) {
      if (tipo === 'success') notificationService.success(mensaje);
      else if (tipo === 'error') notificationService.error(mensaje);
      else if (tipo === 'warning') notificationService.warning(mensaje);
      else notificationService.info(mensaje);
    }
  }
}
</script>

<style scoped>
/* Estilos para modo oscuro */
.dark-theme .card {
  background-color: var(--card-bg);
  border-color: var(--border-color);
  color: var(--text-primary);
}

.dark-theme .card-header {
  background-color: var(--card-header-bg);
  border-bottom-color: var(--border-color);
  color: var(--text-primary);
}

.dark-theme .form-control,
.dark-theme .form-select {
  background-color: var(--input-bg);
  border-color: var(--border-color);
  color: var(--text-primary);
}

.dark-theme .form-control:focus,
.dark-theme .form-select:focus {
  background-color: var(--input-bg);
  border-color: var(--primary-color);
  color: var(--text-primary);
}

.dark-theme .form-label {
  color: var(--text-primary);
}

.dark-theme .form-check-label {
  color: var(--text-primary);
}

/* Estilos específicos del componente */
.card {
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.dark-theme .card {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
}

.card-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0 !important;
  padding: 1.5rem;
}

.card-body {
  padding: 2rem;
}

.btn {
  border-radius: 8px;
  font-weight: 500;
}

.form-control,
.form-select {
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.form-control:focus,
.form-select:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .btn {
    width: 100%;
    margin-bottom: 0.5rem;
  }
}
</style>



